import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * Plan physical hardware independently of Shopify option labels. In particular,
 * an alias means the OLD first hardware until the entire selection is committed.
 * These small pure helpers are exported for the offline regression tests.
 */
export function planSpacerSelection({ quantity, firstKey, secondKey, change, value, valueKey, aliasOnly = false }) {
  const nextQuantity = change === 'quantity' ? value : quantity;
  const nextFirstKey = change === 'first' ? valueKey : firstKey;
  let nextSecondKey = change === 'second' ? valueKey : secondKey || firstKey;
  const pairOnly = nextQuantity === 'Pair of 2';
  const uniform = nextQuantity === 'Set of 4';

  // The dormant second pair cannot create a different Pair-of-2 variant.
  if (pairOnly || aliasOnly) nextSecondKey = nextFirstKey;

  return {
    quantity: nextQuantity,
    firstKey: nextFirstKey,
    secondKey: nextSecondKey,
    useAlias: pairOnly || (uniform && (aliasOnly || nextFirstKey === nextSecondKey)),
  };
}

export function resolveSpacerHardwareOption(options, key, useAlias = false) {
  const candidates = Array.from(options);
  // Product-by-product migration: old products still need the alias, while fully
  // migrated products store the exact first hardware in the dormant second option.
  if (useAlias) {
    const alias = candidates.find((option) => option.hasAttribute('data-pf-alias'));
    if (alias) return alias;
  }
  return candidates.find((option) =>
    !option.hasAttribute('data-pf-alias') && option.dataset.pfHardwareKey === key
  );
}

/**
 * During an in-place migration both representations may still exist on a product.
 * Only the identical physical selection may be retried, never a different seat,
 * length, quantity, or available substitute. A valid sold-out variant is final.
 */
export function equivalentSpacerHardwareOption(options, selected, firstKey, quantity) {
  if (!selected || (quantity !== 'Pair of 2' && quantity !== 'Set of 4')) return undefined;
  const isAlias = selected.hasAttribute('data-pf-alias');
  if (!isAlias && selected.dataset.pfHardwareKey !== firstKey) return undefined;
  return Array.from(options).find((option) => isAlias
    ? !option.hasAttribute('data-pf-alias') && option.dataset.pfHardwareKey === firstKey
    : option.hasAttribute('data-pf-alias')
  );
}

export function isResolvedSpacerVariant(variant, selectedValues) {
  if (!variant || !variant.id || !Array.isArray(variant.options)) return false;
  return variant.options.length === selectedValues.length &&
    variant.options.every((value, index) => String(value) === selectedValues[index]);
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;
  /** @type {AbortController | undefined} */
  #abortController;
  #requestSequence = 0;
  /** @type {number[][]} */
  #checkedIndices = [];
  /** @type {HTMLInputElement[][]} */
  #radios = [];
  #purchaseLocks = new Map();
  #changeHandler = this.variantChanged.bind(this);
  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    this.#checkedIndices = [];
    this.#radios = [];
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input:not([data-pf-presentation])') ?? []);
      this.#radios.push(radios);
      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      this.#checkedIndices.push(initialCheckedIndex === -1 ? [] : [initialCheckedIndex]);
    });
    this.addEventListener('change', this.#changeHandler);
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#changeHandler);
    this.#resizeObserver.disconnect();
  }

  /** Resolve the live physical key, rather than the alias's SSR-time attribute. */
  #hardwareKey(select, firstKey, persistedSelection = false) {
    const option = persistedSelection
      ? Array.from(select?.options || []).find((candidate) => candidate.hasAttribute('selected'))
      : select?.options[select.selectedIndex];
    return option?.hasAttribute('data-pf-alias') ? firstKey : option?.dataset.pfHardwareKey;
  }

  /** Commit a native value AND its selected attribute before option_values is read. */
  #commitNative(select, option, changed) {
    if (!(select instanceof HTMLSelectElement) || !option) return false;
    select.value = option.value;
    this.updateSelectedOption(select);
    changed.add(select);
    return true;
  }

  /**
   * Translate all presentation controls into one canonical option per position.
   * Keep pair-two's physical choice frozen when pair-one previously supplied an alias.
   */
  #translatePresentation(target) {
    const quantity = this.querySelector('select[data-pf-quantity-native]');
    const first = this.querySelector('select[data-pf-bolt-native][data-pf-pair="first"]');
    const second = this.querySelector('select[data-pf-bolt-native][data-pf-pair="second"]');
    const changed = new Set();
    // A visible native conical select has already changed .value when change fires,
    // but the selected ATTRIBUTE still identifies the previously committed choice.
    const nativeHardwareChange = target instanceof HTMLSelectElement && target.hasAttribute('data-pf-bolt-native');
    const oldFirstKey = this.#hardwareKey(first, undefined, nativeHardwareChange && target === first);
    const oldSecondKey = this.#hardwareKey(second, oldFirstKey);
    const aliasOnly = second instanceof HTMLSelectElement &&
      !Array.from(second.options).some((option) => !option.hasAttribute('data-pf-alias'));
    let change;
    let value;
    let valueKey;
    let primary;

    if (target.hasAttribute('data-pf-quantity-control') || target.hasAttribute('data-pf-staggered-control') || target.hasAttribute('data-pf-quantity-native')) {
      if (!(quantity instanceof HTMLSelectElement)) return null;
      change = 'quantity';
      value = target.value;
      // Clicking the already-active Set button must not silently reset a staggered set.
      if (target.hasAttribute('data-pf-quantity-control') && value === 'Set of 4' && quantity.value !== 'Pair of 2') {
        value = quantity.value;
      }
      const option = Array.from(quantity.options).find((candidate) => candidate.value === value);
      if (!option) return null;
      primary = quantity;
      if (!this.#commitNative(quantity, option, changed)) return null;
    } else if (target instanceof HTMLSelectElement && (target.hasAttribute('data-pf-bolt-control') || nativeHardwareChange)) {
      change = target.dataset.pfPair;
      if (change !== 'first' && change !== 'second') return null;
      const selected = target.options[target.selectedIndex];
      valueKey = selected?.dataset.pfHardwareKey;
      if (!valueKey) return null;
      const native = change === 'first' ? first : second;
      if (!(native instanceof HTMLSelectElement)) return null;
      primary = native;
      if (change === 'first') {
        // Prefer the exact source value, preserving generic first-option labels and IDs.
        const option = Array.from(first.options).find((candidate) => candidate.value === selected.value) ||
          resolveSpacerHardwareOption(first.options, valueKey);
        if (!this.#commitNative(first, option, changed)) return null;
      }
    } else {
      return null;
    }

    const plan = planSpacerSelection({
      quantity: quantity instanceof HTMLSelectElement ? quantity.value : 'Set of 4',
      firstKey: oldFirstKey,
      secondKey: oldSecondKey,
      change,
      value,
      valueKey,
      aliasOnly,
    });

    if (second instanceof HTMLSelectElement) {
      const secondOption = resolveSpacerHardwareOption(second.options, plan.secondKey, plan.useAlias);
      if (!this.#commitNative(second, secondOption, changed)) return null;
      // Refresh the alias metadata even if this transaction selected an explicit value.
      for (const option of second.options) {
        if (option.hasAttribute('data-pf-alias')) option.dataset.pfHardwareKey = plan.firstKey || '';
      }
    }

    // R13/R14 are mutually exclusive WITHIN one pair, never across the two pairs.
    this.querySelectorAll('[data-pf-bolt-group]').forEach((group) => {
      const native = group.querySelector('select[data-pf-bolt-native]');
      if (!(native instanceof HTMLSelectElement)) return;
      const key = this.#hardwareKey(native, this.#hardwareKey(first));
      group.querySelectorAll('select[data-pf-bolt-control]').forEach((control) => {
        if (!(control instanceof HTMLSelectElement)) return;
        const option = resolveSpacerHardwareOption(control.options, key) ||
          resolveSpacerHardwareOption(control.options, 'No Bolts|0');
        if (option) control.value = option.value;
      });
    });

    // Stud-only products have no hardware selects; their quantity still resolves natively.
    if (!primary || !changed.size) return null;
    return primary;
  }

  #purchaseForms() {
    const scope = this.closest('.product-details, product-information, featured-product-information, quick-add-dialog, product-card') ||
      this.closest('.shopify-section');
    return scope ? Array.from(scope.querySelectorAll('product-form-component form[action*="/cart/add"], form[action*="/cart/add"]')) : [];
  }

  /** Never leave a previous sellable ID in the form while a sparse tuple resolves. */
  #lockPurchaseSelection() {
    for (const form of this.#purchaseForms()) {
      for (const control of form.querySelectorAll('input[name="id"], button[name="add"][type="submit"]')) {
        if (!this.#purchaseLocks.has(control)) this.#purchaseLocks.set(control, { disabled: control.disabled });
        control.disabled = true;
        if (control instanceof HTMLInputElement) control.value = '';
      }
    }
    this.setAttribute('aria-busy', 'true');
  }

  #restorePurchaseSelection(variant) {
    for (const [control, previous] of this.#purchaseLocks) {
      if (!control.isConnected) continue;
      control.disabled = previous.disabled;
      if (control instanceof HTMLInputElement) {
        control.value = String(variant.id);
        control.disabled = false;
      }
    }
    this.#purchaseLocks.clear();
    this.removeAttribute('aria-busy');
    this.removeAttribute('data-pf-selection-invalid');
    this.querySelector('[data-pf-selection-status]')?.remove();
  }

  #markUnavailable() {
    this.#lockPurchaseSelection();
    this.removeAttribute('aria-busy');
    this.setAttribute('data-pf-selection-invalid', 'true');
    let status = this.querySelector('[data-pf-selection-status]');
    if (!status) {
      status = document.createElement('p');
      status.setAttribute('data-pf-selection-status', '');
      status.setAttribute('role', 'status');
      status.className = 'pf-bolt-help';
      this.querySelector('.variant-picker__form')?.append(status);
    }
    status.textContent = 'That configuration is not available. Please choose another combination.';
  }

  /** @param {Event} event */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;
    let target = event.target;
    const managed = Boolean(this.querySelector('[data-pf-quantity-native], [data-pf-bolt-native]'));

    if (target.hasAttribute('data-pf-presentation') || target.hasAttribute('data-pf-bolt-native') || target.hasAttribute('data-pf-quantity-native')) {
      target = this.#translatePresentation(target);
      if (!target) {
        this.#abortController?.abort();
        this.#requestSequence++;
        this.#markUnavailable();
        return;
      }
    } else if (target instanceof HTMLSelectElement && target.hasAttribute('data-pf-bolt-control')) {
      // Backwards-compatible seam for non-staggered pickers not yet using the new contract.
      const group = target.closest('[data-pf-bolt-group]');
      const native = group?.querySelector('select[data-pf-bolt-native]');
      if (!(native instanceof HTMLSelectElement)) return;
      const value = target.value;
      if (!Array.from(native.options).some((option) => option.value === value)) return;
      group.querySelectorAll('select[data-pf-bolt-control]').forEach((control) => {
        if (control instanceof HTMLSelectElement && (control !== target || value === target.dataset.pfNoBolts)) {
          control.value = target.dataset.pfNoBolts || 'No Bolts';
        }
      });
      native.value = value;
      target = native;
    }

    const selectedOption = target instanceof HTMLSelectElement ? target.options[target.selectedIndex] : target;
    if (!selectedOption) return;
    this.updateSelectedOption(target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage = this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') && !event.target.closest('quick-add-dialog');
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));
    const morphElementSelector = loadsNewProduct ? 'main' :
      isOnFeaturedProductSection ? 'featured-product-information' : undefined;

    const context = {
      managed,
      isOnProductPage,
      newUrl: loadsNewProduct ? newUrl : undefined,
      selectedOptionId: selectedOption.dataset.optionValueId ?? '',
      selectedValues: this.#selectedNativeOptions().map((option) => option.value),
      selectedIds: this.selectedOptionsValues,
    };
    if (managed) this.#lockPurchaseSelection();
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector, context);
    // History deliberately waits for SSR: option data-variant-id can be stale when
    // more than one native option changes together, and can point beyond a sparse tuple.
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];
    if (!radios || !checkedIndices || !fieldset) return null;
    const [currentIndex, previousIndex] = checkedIndices;
    return {
      fieldset, currentIndex, previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    else if (currentIndex !== undefined) fieldset.style.removeProperty('--pill-width-current');
    if (previousWidth) fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    else if (previousIndex !== undefined) fieldset.style.removeProperty('--pill-width-previous');
  }

  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;
    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) this.#applyFieldsetMeasurements(measurements);
  }

  /** @param {string | Element} target */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!targetElement) throw new Error('Target element not found');
      target = targetElement;
    }
    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');
      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];
        if (radios && checkedIndices && fieldset) {
          const [currentIndex, previousIndex] = checkedIndices;
          if (currentIndex !== undefined && radios[currentIndex]) radios[currentIndex].dataset.previousChecked = 'false';
          if (previousIndex !== undefined && radios[previousIndex]) radios[previousIndex].dataset.previousChecked = 'false';
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);
          const [newCurrentIndex, newPreviousIndex] = checkedIndices;
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) radios[newCurrentIndex].dataset.currentChecked = 'true';
          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }
          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }
    if (target instanceof HTMLSelectElement) {
      const newSelectedOption = Array.from(target.options).find((option) => option.value === target.value);
      if (!newSelectedOption) throw new Error('Option not found');
      for (const option of target.options) option.removeAttribute('selected');
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) params.push(`view=${viewParamValue}`);
    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      else params.push(`option_values=${selectedOption.dataset.optionValueId}`);
    }
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };
    const closestSectionId = Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId));
    if (closestSectionId) {
      if (productUrl?.includes('?')) productUrl = productUrl.split('?')[0];
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  #updateResolvedHistory(variant, context, sequence) {
    if (!context?.isOnProductPage) return;
    const url = new URL(window.location.href);
    if (variant?.id) {
      url.searchParams.set('variant', String(variant.id));
      url.searchParams.delete('option_values');
    } else {
      url.searchParams.delete('variant');
      if (context.selectedIds.length) url.searchParams.set('option_values', context.selectedIds.join(','));
    }
    if (context.newUrl) url.pathname = context.newUrl;
    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        if (sequence === this.#requestSequence) history.replaceState({}, '', url.toString());
      });
    }
  }

  fetchUpdatedSection(requestUrl, morphElementSelector, context) {
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;
    const sequence = ++this.#requestSequence;
    return fetch(requestUrl, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Variant request failed (${response.status})`);
        return response.text();
      })
      .then((responseText) => {
        if (controller.signal.aborted || sequence !== this.#requestSequence) return;
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');
        const pickers = Array.from(html.querySelectorAll('variant-picker'));
        const responsePicker = pickers.find((picker) => picker.dataset.blockId === this.dataset.blockId) ||
          pickers.find((picker) => picker.dataset.productId === this.dataset.productId) || pickers[0];
        const textContent = responsePicker?.querySelector('script[type="application/json"]')?.textContent;
        if (!textContent) throw new Error('No selected-variant JSON returned');
        const parsedVariant = JSON.parse(textContent);
        const variant = context?.managed && !isResolvedSpacerVariant(parsedVariant, context.selectedValues)
          ? null : parsedVariant;

        // Option values can coexist while individual variant tuples are renamed.
        // Probe the one equivalent representation only after Shopify reports that
        // the selected tuple does not exist. Never use option data-variant-id here:
        // it was rendered for the previous selection and can be stale.
        if (context?.managed && !variant && !context.hardwareFallbackAttempted) {
          const quantity = this.querySelector('select[data-pf-quantity-native]');
          const first = this.querySelector('select[data-pf-bolt-native][data-pf-pair="first"]');
          const second = this.querySelector('select[data-pf-bolt-native][data-pf-pair="second"]');
          const alternate = equivalentSpacerHardwareOption(
            second?.options || [], second?.options[second.selectedIndex],
            this.#hardwareKey(first), quantity?.value
          );
          if (second instanceof HTMLSelectElement && alternate) {
            second.value = alternate.value;
            this.updateSelectedOption(second);
            return this.fetchUpdatedSection(this.buildRequestUrl(alternate), morphElementSelector, {
              ...context,
              hardwareFallbackAttempted: true,
              selectedOptionId: alternate.dataset.optionValueId ?? '',
              selectedValues: this.#selectedNativeOptions().map((option) => option.value),
              selectedIds: this.selectedOptionsValues,
            });
          }
        }

        this.#updateResolvedHistory(variant, context, sequence);
        if (variant && context?.managed) this.#restorePurchaseSelection(variant);
        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);
          const selectedOptionId = this.selectedOptionId || context?.selectedOptionId;
          if (selectedOptionId) {
            this.dispatchEvent(new VariantUpdateEvent(variant, selectedOptionId, {
              html, productId: this.dataset.productId ?? '', newProduct,
            }));
          }
        }
        // Native Horizon handlers update prices/cart from the actual SSR variant. A
        // null or unavailable tuple is locked again after those synchronous listeners.
        if (context?.managed && !variant) this.#markUnavailable();
        else if (context?.managed && !variant.available) {
          for (const form of this.#purchaseForms()) {
            form.querySelectorAll('button[name="add"][type="submit"]').forEach((button) => { button.disabled = true; });
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
          if (sequence === this.#requestSequence && context?.managed) {
            this.#pendingRequestUrl = undefined;
            this.#markUnavailable();
            this.#updateResolvedHistory(null, context, sequence);
          }
        }
      });
  }

  updateVariantPicker(newHtml) {
    let newProduct;
    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
    if (!newVariantPickerSource) throw new Error('No new variant picker source found');
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;
      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) newProduct = { id: newProductId, url: newProductUrl };
      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }
    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => node instanceof HTMLElement ? node.dataset.key : undefined,
    });
    this.updateVariantPickerCss();
    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);
    for (const measurement of measurements) this.#applyFieldsetMeasurements(measurement);
  }

  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);
    if (!element || !newElement) throw new Error(`No new element source found for ${elementSelector}`);
    morph(element, newElement);
  }

  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');
    if (!main || !newMain) throw new Error('No new main source found');
    morph(main, newMain);
  }

  #selectedNativeOptions() {
    const options = Array.from(this.querySelectorAll(
      'select:not([data-pf-presentation]):not([data-pf-bolt-control]) option[selected], fieldset input:not([data-pf-presentation]):checked'
    ));
    const position = (option) => Number(option.closest('select')?.dataset.pfOptionPosition ||
      option.dataset.pfOptionPosition || option.dataset.inputId?.split('-')[0] || 0);
    return options.sort((a, b) => position(a) - position(b));
  }

  get selectedOption() {
    const option = this.#selectedNativeOptions()[0];
    return option instanceof HTMLInputElement || option instanceof HTMLOptionElement ? option : undefined;
  }

  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;
    if (!optionValueId) throw new Error('No option value ID found');
    return optionValueId;
  }

  get selectedOptionsValues() {
    return this.#selectedNativeOptions().map((option) => {
      const { optionValueId } = option.dataset;
      if (!optionValueId) throw new Error('No option value ID found');
      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);
